/**
 * Auth Context - Manages user session, login/logout, role-based access
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { BACKEND_URL, API } from '@/lib/backendUrl';

const authApi = axios.create({ baseURL: `${API}/api/v2/auth`, withCredentials: true });

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await authApi.get('/me');
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await authApi.post('/login', { email, password });
    setUser(data);
    return data;
  };

  const register = async (email, password, name, role, hotel_id) => {
    const { data } = await authApi.post('/register', { email, password, name, role, hotel_id });
    setUser(data);
    return data;
  };

  const logout = async () => {
    await authApi.post('/logout');
    setUser(null);
  };

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'hotel_manager';

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, isAdmin, isManager, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
